import {
  getDeleteApiUserInstitutionIdUserIdMutationOptions,
  NormalizedApiError,
  type UserDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { userDisplayName, userKeyOf } from './usersModel';

import { useInstitutionOptions } from '@/hooks/useInstitutionOptions';
import { useRegenerateTempPassword } from '@/hooks/useRegenerateTempPassword';
import { useResetCertification } from '@/hooks/useResetCertification';
import { MIN_INSTITUTIONS_TO_MOVE } from '@/ui/pages/UserMove/userMoveModel';
import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';

/** HS-1375: on `DELETE /api/User/{institutionId}/{userId}`, 400 means exactly one thing —
 * blocked by a dependent row (FK violation) — never a request-shape problem. */
const STATUS_BAD_REQUEST = 400;

function RowActions({ row }: { row: UserDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const { id, tenantId } = userKeyOf(row);
  const name = userDisplayName(row);
  // HS-847: delete via server-ops (composite institutionId/userId key, per the
  // generated contract) — invalidates the user list query + toast.
  const del = useEntityDelete({
    entity: 'user',
    mutationOptions: getDeleteApiUserInstitutionIdUserIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
    // HS-1375: see UserDetails.tsx — a dependency-blocked delete (400) gets a title that
    // actually names the situation instead of the generic `errors.validation` copy; the raw
    // `detail` text still shows as the toast's description, unchanged.
    errorTitleKey: (error) =>
      error instanceof NormalizedApiError && error.status === STATUS_BAD_REQUEST
        ? 'users.delete.blockedTitle'
        : undefined,
  });
  const regenerateTempPassword = useRegenerateTempPassword();
  const resetCertification = useResetCertification();
  // HS-1288: no second institution, no destination — the entry hides rather than leading to a
  // screen that can only explain itself. The query is shared with every other row.
  const { options: institutionOptions } = useInstitutionOptions();
  const canMove = institutionOptions.length >= MIN_INSTITUTIONS_TO_MOVE;

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('users.deleteTitle', { name }),
      description: t('users.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ institutionId: tenantId, userId: id });
  }, [del, id, name, tenantId, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      // PUT /api/User/{institutionId}/{userId} carries no permission attribute at all, so this
      // is deliberately stricter than the API: User.Update is the seeded key for the intent.
      permission: 'user.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/edit',
          params: { id, tenantId },
        });
      },
    },
    null,
    {
      label: t('users.actions.manageScope'),
      permission: 'user.manageScopes',
      icon: 'bank',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/access',
          params: { id, tenantId },
        });
      },
    },
    {
      label: t('users.actions.managePermissions'),
      permission: 'user.managePermissions',
      icon: 'users-02',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/permissions',
          params: { id, tenantId },
        });
      },
    },
    ...(canMove
      ? [
          {
            label: t('users.actions.moveUser'),
            // Mirrors the route gate; the endpoint also asks `Institution.AdministerHierarchy`,
            // which a menu entry cannot express alongside this one.
            permission: 'user.move' as const,
            icon: 'switch-horizontal-01' as const,
            onClick: () => {
              void navigate({
                to: '/users/$id/$tenantId/move',
                params: { id, tenantId },
              });
            },
          },
        ]
      : []),
    {
      // HS-1224: the second entry point for the shared regenerate flow (the other is the user
      // edit screen). The temporary password is shown once and cannot be read back afterwards.
      label: t('users.regeneratePassword.action'),
      // The endpoint asks for User.Edit, which the seeder never creates — so today only
      // FullAccess can actually call it. Gate on the intent key rather than encode the backend
      // bug here; see the User.Edit/User.Update discrepancy in the API's own catalogue notes.
      permission: 'user.regeneratePassword',
      icon: 'key-01',
      onClick: () => {
        void regenerateTempPassword({ institutionId: tenantId, userId: id, userName: name });
      },
    },
    {
      // HS-1455: the users list is the only route to this for anyone whose certification is not
      // coming due — the dashboard panel that also offers it lists nobody else.
      //
      // Every scope the person holds (HS-1491). A row here is a `UserDTO` and carries no
      // certification scope, and `labId: null` only ever reached the institution-wide programme,
      // so a laboratory pass survived and the person stayed certified.
      label: t('users.actions.resetCertification'),
      permission: 'certification.reset',
      icon: 'refresh-cw-05',
      onClick: () => {
        void resetCertification({
          userId: id,
          institutionId: tenantId,
          allScopes: true,
          name,
          description: t('certifications.reset.descriptionAll'),
        });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'user.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <>
      <Shared items={items} />
    </>
  );
}

export default RowActions;
